import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, Marker } from "react-leaflet";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import GeoSearchAutocomplete from "@/components/GeoSearchAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import PrivacyPromptDialog from "@/components/PrivacyPromptDialog";
import VideoUploader from "@/components/VideoUploader";
import { toast } from "sonner";
import { Camera, MapPin, ShieldAlert, Loader2, X, AlertTriangle, RefreshCw } from "lucide-react";
import { TileHealth, pinIcon, ClickToSelect, FlyTo } from "@/components/MapCore";
import ColorWizard from "@/components/ColorWizard";
import { useAssistantPageTips } from "@/components/ContextAssistant";

const RISK_META = {
  green: { labelKey: "risk_green_label", descKey: "risk_green_short_desc", color: "bg-emerald-500" },
  yellow: { labelKey: "risk_yellow_label", descKey: "risk_yellow_short_desc", color: "bg-yellow-500" },
  red: { labelKey: "risk_red_label", descKey: "risk_red_short_desc", color: "bg-red-500" },
};

export default function ReportCreate() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  useAssistantPageTips([
    { id: "rc-wizard", text: t("tip_rc_wizard") },
    { id: "rc-privacy", text: t("tip_rc_privacy") },
    { id: "rc-after-photo", text: t("tip_rc_after_photo") },
  ]);

  const [cats, setCats] = useState({});
  const [category, setCategory] = useState("waste");
  const [subcategory, setSubcategory] = useState("");
  // Gift structured fields
  const [giftTaxonomy, setGiftTaxonomy] = useState({});
  const [giftCategory, setGiftCategory] = useState("");
  const [giftSubcategory, setGiftSubcategory] = useState("");
  const [giftOtherDesc, setGiftOtherDesc] = useState("");
  const [giftCondition, setGiftCondition] = useState("good");
  const [giftKeywords, setGiftKeywords] = useState("");
  useEffect(() => {
    api.get("/gift-taxonomy").then((r) => setGiftTaxonomy(r.data || {})).catch(() => {});
  }, []);
  const giftSubs = giftTaxonomy[giftCategory] || [];
  const [description, setDescription] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [risk, setRisk] = useState(null);
  const [containsSensitive, setContainsSensitive] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);  // Phase 4 — storage paths
  const [uploading, setUploading] = useState(false);
  // Geographic search — Paese / Provincia (regione, dipartimento, contea) / Comune.
  // Uses the same GeoNames/Nominatim autocomplete as the map filters.
  const [geoArea, setGeoArea] = useState(null);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [tileErr, setTileErr] = useState(false);
  const [tileKey, setTileKey] = useState(0);
  const [geoDenied, setGeoDenied] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/auth"); return; }
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { setLat(pos.coords.latitude.toFixed(6)); setLng(pos.coords.longitude.toFixed(6)); },
        (err) => { if (err && err.code === 1) setGeoDenied(true); }
      );
    }
  }, [user, nav, authLoading]);

  useEffect(() => {
    if (cats[category] && cats[category].length > 0 && !cats[category].includes(subcategory)) {
      setSubcategory(cats[category][0]);
    }
  }, [category, cats, subcategory]);

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, { path: data.path, url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }]);
      toast.success(t("photo_uploaded"));
    } catch (e) {
      toast.error(t("photo_upload_failed"));
    } finally { setUploading(false); }
  };

  const [privacyOpen, setPrivacyOpen] = useState(false);

  const submit = () => {
    if (!lat || !lng) { toast.error(t("gps_missing")); return; }
    if (!subcategory) { toast.error(t("select_subcategory")); return; }
    if (!description || description.length < 8) { toast.error(t("description_too_short")); return; }
    if (!photos.length) { toast.error(t("photo_required")); return; }
    if (!risk) { toast.error(t("cw_required")); return; }
    // All client-side validations passed — ask the privacy question before
    // hitting the API. The actual POST happens inside `finalizeSubmit`.
    setPrivacyOpen(true);
  };

  const finalizeSubmit = async (privacyFields) => {
    setPrivacyOpen(false);
    setSubmitBusy(true);
    try {
      const { data } = await api.post("/reports", {
        category, subcategory, description,
        lat: parseFloat(lat), lng: parseFloat(lng),
        risk_level: risk,
        photos: photos.map((p) => p.path),
        videos: videos,  // Phase 4 storage paths
        contains_sensitive: containsSensitive,
        // iter68 privacy prompt payload — chosen by the user in PrivacyPromptDialog
        ...privacyFields,
        ...(subcategory === "gift" ? {
          gift_category: giftCategory || null,
          gift_subcategory: giftSubcategory || null,
          gift_other_description: giftOtherDesc || null,
          gift_condition: giftCondition || null,
          gift_keywords: giftKeywords ? giftKeywords.split(",").map((s) => s.trim()).filter(Boolean) : [],
        } : {}),
      });
      // iter79: YES → always into review (no bypass anymore)
      if (privacyFields.privacy_prompt_answer === true) {
        toast.success(t("pp_sent_review_toast"));
      } else {
        toast.success(t("report_submitted"));
      }
      nav(`/report/${data.report_id}?new=1`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("generic_error"));
    } finally { setSubmitBusy(false); }
  };

  const subs = cats[category] || [];

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="report-create-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <BackButton className="mb-4" testid="report-back-btn" />
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold">{t("new_report")}</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">{t("new_report_title")}</h1>
          <p className="mt-2 text-slate-600">{t("new_report_desc")}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-6 shadow-sm">
          {/* Photos */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("upload_photos")} *</Label>
            <div className="mt-2 grid grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 group" data-testid={`photo-${i}`}>
                  <img src={p.url} alt="upload" className="w-full h-full object-cover" />
                  <button onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-black">
                    <X size={12} />
                  </button>
                </div>
              ))}
              <label className="aspect-square rounded-xl border-2 border-dashed border-emerald-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700" data-testid="photo-take-btn">
                {uploading ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
                <span className="text-[11px] mt-1 font-semibold text-center px-1">{t("take_photo")}</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadPhoto(f);
                    else if (e.target.files && e.target.files.length === 0) {
                      // User cancelled — no-op; if permission is denied, some browsers give no file
                    }
                    e.target.value = "";
                  }}
                />
              </label>
              <label className="aspect-square rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 text-slate-500 hover:text-emerald-700" data-testid="photo-gallery-btn">
                {uploading ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
                <span className="text-[11px] mt-1 text-center px-1">{t("from_gallery")}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadPhoto(f);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
          </div>

          {/* Phase 4 — optional videos */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("video_upload_label")}</Label>
            <div className="mt-1.5">
              <VideoUploader value={videos} onChange={setVideos} testidPrefix="report-video" />
            </div>
            <div className="text-[10px] text-slate-500 mt-1">{t("video_privacy_notice")}</div>
          </div>

          {/* Category + subcategory */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("category")}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="report-category" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(cats).map((c) => <SelectItem key={c} value={c}>{t(`cat_${c}`) || c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("subcategory")}</Label>
              <Select value={subcategory} onValueChange={setSubcategory}>
                <SelectTrigger data-testid="report-subcategory" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {subs.map((s) => <SelectItem key={s} value={s}>{t(`sub_${s}`) || s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {subcategory === "gift" && (
            <div className="mt-4 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 space-y-3" data-testid="gift-structured-block">
              <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">Dettagli oggetto da regalare</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Categoria oggetto</Label>
                  <Select value={giftCategory} onValueChange={(v) => { setGiftCategory(v); setGiftSubcategory(""); }}>
                    <SelectTrigger data-testid="gift-category" className="mt-1 h-10 rounded-xl"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(giftTaxonomy).map((k) => (
                        <SelectItem key={k} value={k} data-testid={`gift-cat-${k}`}>{k}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Sottocategoria</Label>
                  <Select value={giftSubcategory} onValueChange={setGiftSubcategory} disabled={!giftCategory}>
                    <SelectTrigger data-testid="gift-subcategory" className="mt-1 h-10 rounded-xl"><SelectValue placeholder={giftCategory ? "Seleziona…" : "Prima la categoria"} /></SelectTrigger>
                    <SelectContent>
                      {giftSubs.map((s) => <SelectItem key={s} value={s} data-testid={`gift-sub-${s}`}>{s.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Stato dell'oggetto</Label>
                  <Select value={giftCondition} onValueChange={setGiftCondition}>
                    <SelectTrigger data-testid="gift-condition" className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Nuovo</SelectItem>
                      <SelectItem value="like_new">Come nuovo</SelectItem>
                      <SelectItem value="good">Buono</SelectItem>
                      <SelectItem value="used">Usato</SelectItem>
                      <SelectItem value="fair">Discreto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Parole chiave (separate da virgola)</Label>
                  <Input data-testid="gift-keywords" value={giftKeywords} onChange={(e) => setGiftKeywords(e.target.value)} placeholder="es. legno, vintage, ikea" className="mt-1 h-10 rounded-xl" />
                </div>
              </div>
              {(giftCategory === "other" || giftSubcategory === "other" || giftSubcategory === "custom") && (
                <div>
                  <Label className="text-xs text-slate-600">Descrizione aggiuntiva</Label>
                  <Input data-testid="gift-other-desc" value={giftOtherDesc} onChange={(e) => setGiftOtherDesc(e.target.value)} placeholder="Specifica il tipo di oggetto" className="mt-1 h-10 rounded-xl" />
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("description")} *</Label>
            <Textarea data-testid="report-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("describe_placeholder")} className="mt-1.5 min-h-[120px] rounded-xl" />
          </div>

          {/* Territory search (Paese / Provincia / Comune) — usa lo stesso
              autocompletamento geografico dei filtri della mappa. Dopo la
              selezione, la mappa vola sull'area scelta; l'utente può poi
              spostare il punto sulla mappa per correggerlo. */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("filter_area") || "Ricerca territorio"}</Label>
            <div className="mt-1.5">
              <GeoSearchAutocomplete
                testid="report-geo-search"
                value={geoArea}
                onChange={(area) => {
                  setGeoArea(area);
                  if (area && typeof area.lat === "number" && typeof area.lng === "number") {
                    setLat(Number(area.lat).toFixed(6));
                    setLng(Number(area.lng).toFixed(6));
                  }
                }}
                placeholder="Paese, provincia o comune…"
              />
              {geoArea && (
                <div className="mt-1 text-[10px] text-emerald-800 font-semibold" data-testid="report-geo-selected">
                  Territorio: {geoArea.display}
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          <div className="grid md:grid-cols-3 gap-4 items-end">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("latitude")} *</Label>
              <Input data-testid="report-lat" type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("longitude")} *</Label>
              <Input data-testid="report-lng" type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <Button
              data-testid="report-geo-btn"
              type="button" variant="outline" className="h-11 rounded-full hover:bg-slate-900 hover:text-white"
              onClick={() => {
                if (!navigator.geolocation) { setGeoDenied(true); toast.error(t("gps_unavailable")); return; }
                navigator.geolocation.getCurrentPosition(
                  (p) => { setLat(p.coords.latitude.toFixed(6)); setLng(p.coords.longitude.toFixed(6)); setGeoDenied(false); toast.success(t("gps_updated")); },
                  (err) => { if (err && err.code === 1) setGeoDenied(true); toast.error(t("gps_pick_manual")); }
                );
              }}
            ><MapPin size={16} className="mr-2" /> {t("detect")}</Button>
          </div>

          {/* Interactive map picker */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
              <MapPin size={12} /> {t("position_on_map")}
            </Label>
            {geoDenied && (
              <div className="mt-2 p-2.5 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-900 flex items-start gap-2" data-testid="geo-denied-msg">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{t("geo_denied_msg")}</span>
              </div>
            )}
            <div className="mt-2 relative rounded-2xl overflow-hidden border border-slate-200" style={{ height: 320 }} data-testid="report-map-picker">
              <MapContainer
                key={`picker-${tileKey}`}
                center={lat && lng ? [parseFloat(lat), parseFloat(lng)] : [41.9028, 12.4964]}
                zoom={lat && lng ? 15 : 5}
                scrollWheelZoom={true}
                style={{ width: "100%", height: "100%" }}
              >
                <TileHealth onError={() => setTileErr(true)} onLoad={() => setTileErr(false)} />
                <ClickToSelect onSelect={(latlng) => { setLat(latlng.lat.toFixed(6)); setLng(latlng.lng.toFixed(6)); toast.success(t("position_set")); }} />
                {lat && lng && (
                  <>
                    <FlyTo center={[parseFloat(lat), parseFloat(lng)]} zoom={15} />
                    <Marker position={[parseFloat(lat), parseFloat(lng)]} icon={pinIcon("#EF4444")} />
                  </>
                )}
              </MapContainer>
              {tileErr && (
                <div className="absolute inset-x-2 top-2 z-[1200] glass-panel rounded-xl p-2.5 flex items-center gap-2" data-testid="picker-tile-error">
                  <AlertTriangle size={14} className="text-red-600 shrink-0" />
                  <span className="text-xs text-slate-800 flex-1">{t("tiles_not_loaded")}</span>
                  <Button data-testid="picker-retry-btn" type="button" onClick={() => { setTileErr(false); setTileKey((k) => k + 1); }} size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800 hover:text-white text-white h-7 text-xs">
                    <RefreshCw size={10} className="mr-1" /> {t("map_retry")}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Risk level — guided wizard (utente non sceglie liberamente il colore) */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("risk_level")} *</Label>
            <div className="mt-2">
              <ColorWizard value={risk} onChange={(c) => setRisk(c || "green")} />
            </div>
          </div>

          {/* AI check runs automatically in background AFTER submit
              (spam/coerenza/moderazione). Non modifica il colore scelto
              dall'utente. Nessun pulsante manuale in questa schermata. */}

          {/* Sensitive */}
          <div className="flex items-start gap-2">
            <Checkbox id="sensitive" checked={containsSensitive} onCheckedChange={setContainsSensitive} data-testid="sensitive-check" />
            <label htmlFor="sensitive" className="text-sm text-slate-700 leading-relaxed">
              <b className="text-slate-900">{t("sensitive_content_b")}</b> {t("sensitive_content_desc")}
            </label>
          </div>

          <div className="p-3 rounded-xl bg-yellow-50 border border-yellow-200 text-xs text-yellow-900 flex items-start gap-2">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span>{t("emergency_notice")}</span>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button data-testid="submit-report-btn" onClick={submit} disabled={submitBusy} size="lg" className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white h-12 px-6">
              {submitBusy ? <Loader2 className="animate-spin mr-2" size={16} /> : null} {t("submit")}
            </Button>
            <Button data-testid="cancel-report-btn" onClick={() => nav(-1)} variant="ghost" className="rounded-full hover:bg-slate-100 hover:text-slate-900">{t("cancel")}</Button>
          </div>
        </div>
      </div>
      <PrivacyPromptDialog
        open={privacyOpen}
        onCancel={() => setPrivacyOpen(false)}
        onDecided={finalizeSubmit}
      />
    </div>
  );
}
